import { Button, CircularProgress, styled } from '@ethings/ui-toolkit';
import { FC, Fragment, useMemo, type JSX } from 'react';
import { DataExportLauncher } from '../../components';
import { FilterBar } from '../../components/filter';
import { ListLayoutDesktop } from './index';
import { InternalProps } from './view.types';

const StyledCircularProgress = styled(CircularProgress)({
  marginRight: '0.5rem',
});

export const ListPageViewDesktop: FC<InternalProps> = ({
  actions,
  appliedFiltersComponents,
  clearAllFiltersComponent,
  containerSize,
  dataExportConfig,
  dataExportOverrides,
  extraActions,
  filterDisabled,
  filterDrawerComponent,
  filtersLength,
  icon,
  listContent,
  openFilter,
  paginationComponent,
  scrollRef,
  search,
  setFilterOpen,
  showFilterButton,
  subtitleComponent,
  tableContent,
  title,
  ...props
}): JSX.Element => {
  const filterButtonComponent = (
    <FilterBar
      disabled={filterDisabled}
      filtersLength={filtersLength}
      onFilterClicked={() => setFilterOpen(!openFilter)}
    />
  );

  const actionComponents = useMemo(
    () => actions?.length
      ? actions.map((action, i) => {
        if (action.buttonNode) {
          return <Fragment key={i}>{action.buttonNode}</Fragment>;
        }
        return (
          <Button
            key={i}
            data-testid={action.testId ?? 'action-button'}
            disabled={action.disabled || action.isLoading}
            href={action.href}
            onClick={action.onClick}
            startIcon={action.icon}
            variant={action.buttonVariant}
          >
            {action.isLoading
              ? <>
                <StyledCircularProgress color="inherit" role="status" size="0.8rem" />
                <span>{action.label || <>&nbsp;</>}</span>
              </>
              : action.label
            }
          </Button>
        );
      })
      : [],
    [actions],
  );

  const dataExportComponent = dataExportConfig?.isEnabled ? (
    <DataExportLauncher
      key="data-export-launcher"
      dataExportSettings={dataExportConfig.settings}
      dataExportOverrides={dataExportOverrides}
      isMobile={false}
    />
  ) : null;

  return (
    <ListLayoutDesktop
      actions={actionComponents}
      containerSize={containerSize}
      clearAllFiltersButton={clearAllFiltersComponent}
      content={listContent ?? tableContent ?? <></>}
      dataExportComponent={dataExportComponent}
      data-testid={props['data-testid']}
      extraActions={extraActions}
      filterButton={filterButtonComponent}
      filterContent={filterDrawerComponent}
      filters={appliedFiltersComponents}
      icon={icon}
      pagination={listContent && paginationComponent}
      scrollRef={scrollRef}
      search={search}
      setFilterOpen={setFilterOpen}
      showFilterButton={showFilterButton}
      showFilters={openFilter}
      subtitle={subtitleComponent}
      title={title}
    />
  );
};
